import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PasswordInput } from "../components/Inputs";
import { useLoaderStore } from "../context";
import useUserStore from "../context/useUserStore";
import { useAlert, useApi } from "../hooks";
import { API } from "../utils/constants";

/**
 * ChangePassword — Forced password change page.
 *
 * Flow (CON-002 §1):
 * - Required when currentUser.mustChangePassword === true.
 * - POST /api/auth/change-password → { message }
 * - On success: clears mustChangePassword flag → redirects to /admin
 * - On 401: shows "current password incorrect" error
 * - On 400: shows "password requirements not met" error
 */
export default function ChangePassword() {
  const alert = useAlert();
  const { post } = useApi();
  const { currentUser, login, accessToken } = useUserStore();
  const { setShowLoader } = useLoaderStore();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const isValid = () => {
    if (!currentPassword.trim()) {
      alert("Validation error", "Please enter your current password.", "warning");
      return false;
    }
    if (!newPassword.trim() || newPassword.trim().length < 8) {
      alert("Validation error", "New password must be at least 8 characters.", "warning");
      return false;
    }
    if (newPassword !== confirmPassword) {
      alert("Validation error", "New password and confirmation do not match.", "warning");
      return false;
    }
    return true;
  };

  const handleChangePassword = async () => {
    if (!isValid()) return;
    setShowLoader(true);

    try {
      await post(API.AUTH.CHANGE_PASSWORD, {
        currentPassword,
        newPassword,
      });

      // Clear mustChangePassword flag — update user in store
      if (currentUser) {
        login({ ...currentUser, mustChangePassword: false }, accessToken);
      }

      alert("Password changed", "Your password has been updated.", "success");
      navigate("/admin");
    } catch (err) {
      if (err?.status === 401) {
        alert("Error", "Current password is incorrect.", "error");
      } else {
        alert(
          "Error",
          err?.data?.detail || "Password does not meet requirements.",
          "error"
        );
      }
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-cream flex items-center justify-center px-4"
      data-testid="change-password-page"
    >
      <div className="w-full max-w-sm">
        {/* Heading */}
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl text-charcoal leading-tight">
            Set Your Password
          </h1>
          <p className="font-body text-charcoal/60 mt-2 text-sm">
            You must change your password before continuing.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white border border-aluminum/30 rounded-sm shadow-sm p-8 space-y-5">
          <h2 className="font-heading text-xl text-charcoal tracking-wider">CHANGE PASSWORD</h2>

          <div>
            <PasswordInput
              label="Current Password"
              name="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              data-testid="current-password-input"
            />
          </div>

          <div>
            <PasswordInput
              label="New Password"
              name="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="new-password-input"
            />
          </div>

          <div>
            <PasswordInput
              label="Confirm New Password"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleChangePassword(); }}
              data-testid="confirm-password-input"
            />
          </div>

          <button
            onClick={handleChangePassword}
            className="w-full bg-teal text-white font-heading tracking-widest text-lg py-2.5 rounded-sm hover:bg-teal/80 active:scale-95 transition-all"
            data-testid="change-password-button"
          >
            UPDATE PASSWORD
          </button>
        </div>
      </div>
    </div>
  );
}
